import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'

import {
  apiTokenDtoSchema,
  apiTokenListDtoSchema,
  createdApiTokenDtoSchema,
  type ApiTokenDto,
  type ApiTokenListDto,
  type CreatedApiTokenDto,
} from '@lib/dto/api-token.dto'
import type { CreateApiTokenInput } from '@lib/input/api-token.input'
import { mcpWriteListDtoSchema, type McpWriteListDto } from '@lib/dto/mcp-write.dto'
import type { IApiTokenService } from '@/types/ApiTokenService.types'

/**
 * A person's own API tokens. `create` is the only call that ever carries the
 * secret — nothing stores it, here or on the server, so it exists in the
 * response and then only wherever the person put it.
 */
class ApiTokenService implements IApiTokenService {
  async list(): Promise<ApiTokenListDto> {
    try {
      return await api.get('/api-tokens', apiTokenListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list API tokens', {}, error)
      throw error
    }
  }

  async create(input: CreateApiTokenInput): Promise<CreatedApiTokenDto> {
    try {
      return await api.post('/api-tokens', input, createdApiTokenDtoSchema)
    } catch (error) {
      // Deliberately not logging the input: it is about to become a credential.
      logRequestFailure('Failed to create an API token', {}, error)
      throw error
    }
  }

  async revoke(id: string): Promise<ApiTokenDto> {
    try {
      return await api.delFor(`/api-tokens/${id}`, apiTokenDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to revoke an API token', { id }, error)
      throw error
    }
  }

  async changes(id: string): Promise<McpWriteListDto> {
    try {
      return await api.get(`/api-tokens/${id}/changes`, mcpWriteListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list what an API token changed', { id }, error)
      throw error
    }
  }
}

export const apiTokenService = new ApiTokenService()
